/**
 * One registry for every floating panel the calendar page (and the app shell)
 * persists — positions and open/hidden flags — plus the "Reset panels" broadcast.
 *
 * Why an event AND direct localStorage writes: the `storage` event only fires in
 * OTHER tabs, so same-tab components must be told explicitly. Reset writes the
 * defaults here, then dispatches RESET_FLOATING_PANELS_EVENT; every floating
 * component (Calendar Settings panel, the app-shell FloatingNavbar FAB, the
 * floating chat widget) listens and re-reads its defaults.
 *
 * The app-shell components deliberately do NOT import this file (module import
 * contract — they can't reach into a module folder). The EVENT NAME and the
 * storage keys are the contract; they hardcode the same strings.
 */

export const RESET_FLOATING_PANELS_EVENT = "tms:reset-floating-panels";

/** Keys owned by the calendars module. */
export const SETTINGS_PANEL_POS_KEY = "calendars.settingsPanel.pos";
export const SETTINGS_PANEL_OPEN_KEY = "calendars.settingsPanel.open";
export const SETTINGS_FAB_HIDDEN_KEY = "calendars.settingsFab.hidden";
export const SETTINGS_FAB_POS_KEY = "calendars.settingsFab.pos";
/** The calendar-menu launcher (FloatingCalendarMenu — the old toolbar, 2026-08-15). */
export const CAL_MENU_FAB_POS_KEY = "calendars.menuFab.pos";
export const CAL_MENU_FAB_HIDDEN_KEY = "calendars.menuFab.hidden";
export const CAL_MENU_OPEN_KEY = "calendars.menu.open";
/** The module-nav launcher (FloatingCalendarNav — the breadcrumb's links, 2026-08-15). */
export const CAL_NAV_FAB_POS_KEY = "calendars.navFab.pos";
export const CAL_NAV_FAB_HIDDEN_KEY = "calendars.navFab.hidden";
export const CAL_NAV_OPEN_KEY = "calendars.nav.open";

/**
 * Keys owned by app-shell components, cleared here so ONE button recovers every
 * floater on the page. Shapes:
 * - `tms_floating_navbar_pos_<clientId>` / `floatingchat.fab.pos.<clientId>` /
 *   `floatingchat.panel.pos.<clientId>` — {left,top} JSON (useDraggableFloat)
 * - `tms_floating_navbar_hidden_<clientId>` / `floatingchat.open.<clientId>` /
 *   `floatingchat.hidden.<clientId>` — "1"/"0" flags
 */
const APP_SHELL_KEY_PREFIXES = [
    "tms_floating_navbar_pos_",
    "tms_floating_navbar_hidden_",
    "floatingchat.fab.pos",
    "floatingchat.panel.pos",
    "floatingchat.panel.size", // {w,h} JSON — user-resized panel (2026-09-07)
    "floatingchat.input.h", // composer height in px (2026-09-07)
    "floatingchat.open",
    "floatingchat.hidden",
];

/**
 * Reset every floating panel on the page: forget stored positions + visibility
 * flags, then broadcast so mounted components snap back immediately.
 * Returns the number of storage keys cleared (for the toast).
 */
export function resetFloatingPanels(): number {
    let cleared = 0;
    try {
        const doomed: string[] = [
            SETTINGS_PANEL_POS_KEY,
            SETTINGS_PANEL_OPEN_KEY,
            SETTINGS_FAB_HIDDEN_KEY,
            SETTINGS_FAB_POS_KEY,
            CAL_MENU_FAB_POS_KEY,
            CAL_MENU_FAB_HIDDEN_KEY,
            CAL_MENU_OPEN_KEY,
            CAL_NAV_FAB_POS_KEY,
            CAL_NAV_FAB_HIDDEN_KEY,
            CAL_NAV_OPEN_KEY,
        ];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && APP_SHELL_KEY_PREFIXES.some((p) => key.startsWith(p))) doomed.push(key);
        }
        doomed.forEach((key) => {
            if (localStorage.getItem(key) != null) {
                localStorage.removeItem(key);
                cleared++;
            }
        });
    } catch {
        /* storage unavailable — the event alone still resets mounted components */
    }
    window.dispatchEvent(new CustomEvent(RESET_FLOATING_PANELS_EVENT));
    return cleared;
}
